import { useMemo, useState } from 'react';
import { Clock3, HeartPulse, Plus, ShieldCheck, Stethoscope, Trash2, Users } from 'lucide-react';
import {
    staffLabel, staffRoleLabel,
    type OrCase, type RoomResource, type StaffAssignment, type StaffMember, type StaffRole,
} from './useAscSchedule';

const PHYSICIAN_CREDENTIALS = ['MD', 'DO', 'DPM', 'DMD', 'DDS'];
const SUPPORT_CREDENTIALS = ['RN', 'LPN', 'CST', 'ST', 'RT'];

function isSurgeon(p: StaffMember): boolean {
    return (p.specialty ?? '').toLowerCase().includes('surg');
}
function isAnesthesia(p: StaffMember): boolean {
    const text = `${p.specialty ?? ''} ${p.title ?? ''} ${p.credential ?? ''}`.toLowerCase();
    return text.includes('anesth') || text.includes('crna');
}
function isSupport(p: StaffMember): boolean {
    const credential = (p.credential ?? '').toUpperCase();
    const text = `${p.specialty ?? ''} ${p.title ?? ''}`.toLowerCase();
    return SUPPORT_CREDENTIALS.some((c) => credential.includes(c)) || /nurs|scrub|tech|pacu|periop/.test(text);
}
function professionalLine(p: StaffMember): string {
    return [p.specialty, p.title].filter(Boolean).join(' · ') || (p.credential && PHYSICIAN_CREDENTIALS.includes(p.credential.toUpperCase()) ? 'Physician' : 'Clinical staff');
}
function timeLabel(ts: string | null): string {
    return ts ? new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Open shift';
}

function RosterCard({ title, icon, people, accent, scheduledNames }: {
    title: string;
    icon: React.ReactNode;
    people: StaffMember[];
    accent: string;
    scheduledNames?: Set<string>;
}) {
    return (
        <div className="rounded-xl border border-pt-border bg-pt-panel overflow-hidden">
            <div className="px-4 py-3 border-b border-pt-border/70 flex items-center gap-2">
                <span className={accent}>{icon}</span><h3 className="text-sm font-medium text-pt-text">{title}</h3>
                <span className="ml-auto text-[10px] text-pt-text-dim">{people.length} active</span>
            </div>
            <div className="divide-y divide-pt-border/50 max-h-[260px] overflow-y-auto">
                {people.map((p) => {
                    const name = staffLabel(p);
                    const scheduled = scheduledNames?.has(name) || scheduledNames?.has([p.first_name, p.last_name].filter(Boolean).join(' '));
                    return (
                        <div key={p.provider_id} className="px-4 py-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full border border-pt-gold/35 bg-pt-gold/10 text-pt-gold text-[11px] font-semibold flex items-center justify-center shrink-0">{p.first_name?.[0]}{p.last_name?.[0]}</div>
                            <div className="min-w-0 flex-1"><div className="text-xs text-pt-text truncate">{name}</div><div className="text-[10px] text-pt-text-muted truncate">{professionalLine(p)}</div></div>
                            {scheduled && <span className="text-[9px] uppercase tracking-wider rounded bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5">On schedule</span>}
                        </div>
                    );
                })}
                {people.length === 0 && <div className="px-4 py-8 text-center text-xs text-pt-text-muted">No active roster entries in this category. Add credentials in Admin → Providers.</div>}
            </div>
        </div>
    );
}

export function StaffRosters({
    dateStr, cases, rooms, staff, assignments, loading, onSave, onRemove, saving, removing,
}: {
    dateStr: string;
    cases: OrCase[];
    rooms: RoomResource[];
    staff: StaffMember[];
    assignments: StaffAssignment[];
    loading: boolean;
    onSave: (input: { dateStr: string; resource: RoomResource; role: StaffRole; staff: StaffMember; shiftStart?: string; shiftEnd?: string; notes?: string }) => void;
    onRemove: (assignmentId: string) => void;
    saving: boolean;
    removing: boolean;
}) {
    const [dialogRoom, setDialogRoom] = useState<RoomResource | null>(null);
    const surgeons = useMemo(() => staff.filter(isSurgeon), [staff]);
    const anesthesia = useMemo(() => staff.filter(isAnesthesia), [staff]);
    const support = useMemo(() => staff.filter((p) => isSupport(p) && !isAnesthesia(p)), [staff]);
    const scheduledSurgeons = useMemo(() => new Set(cases.map((c) => c.surgeon_name).filter((v): v is string => !!v)), [cases]);
    const scheduledAnesthesia = useMemo(() => new Set(cases.map((c) => c.anesthesiologist_name).filter((v): v is string => !!v)), [cases]);
    const scheduledSupport = useMemo(() => new Set(cases.flatMap((c) => [c.circulating_nurse_name, c.scrub_tech_name]).filter((v): v is string => !!v)), [cases]);
    const staffedResources = new Set(assignments.map((a) => a.resource_id));
    const assignmentRooms: RoomResource[] = [
        ...rooms.filter((r) => ['preop_bay', 'or_suite', 'pacu_bay'].includes(r.resource_kind)),
        { resource_id: 'discharge-area', resource_kind: 'discharge_area', display_name: 'Discharge Area' },
    ];

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <RosterCard title="Surgeon roster" icon={<Stethoscope size={16} />} people={surgeons} scheduledNames={scheduledSurgeons} accent="text-pt-gold" />
                <RosterCard title="Anesthesiology roster" icon={<HeartPulse size={16} />} people={anesthesia} scheduledNames={scheduledAnesthesia} accent="text-pt-cyan" />
                <RosterCard title="Support staff roster" icon={<Users size={16} />} people={support} scheduledNames={scheduledSupport} accent="text-emerald-400" />
            </div>

            <section className="rounded-xl border border-pt-border bg-pt-panel overflow-hidden">
                <div className="px-4 py-3 border-b border-pt-border/70 flex flex-wrap items-center gap-3">
                    <div className="mr-auto"><h2 className="text-sm font-semibold text-pt-text">ASC area staffing</h2><p className="text-[11px] text-pt-text-muted">Assign accountable coverage across Pre-Op, Operating, Post-Op/PACU, and Discharge.</p></div>
                    <button type="button" onClick={() => setDialogRoom(assignmentRooms[0] ?? null)} disabled={assignmentRooms.length === 0}
                        className="text-xs font-medium rounded bg-pt-gold text-pt-darker px-3 py-2 hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5"><Plus size={14} /> Assign staff</button>
                </div>
                {loading ? <div className="px-5 py-10 text-sm text-pt-text-muted">Loading staffing roster…</div> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
                        {assignmentRooms.map((r) => {
                            const rows = assignments.filter((a) => a.resource_id === r.resource_id);
                            const areaLine = r.resource_kind === 'preop_bay' ? 'Pre-Op coverage'
                                : r.resource_kind === 'pacu_bay' ? 'Post-Op / PACU coverage'
                                    : r.resource_kind === 'discharge_area' ? 'Discharge coverage' : 'Operating room team';
                            return (
                                <div key={r.resource_id} className={['rounded-lg border p-3', rows.length ? 'border-pt-cyan/35 bg-pt-darker/55' : 'border-pt-border bg-pt-darker/30'].join(' ')}>
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <div><div className="text-sm font-medium text-pt-text">{r.display_name}</div><div className="text-[10px] text-pt-text-dim">{areaLine}</div></div>
                                        <button type="button" onClick={() => setDialogRoom(r)} aria-label={`Assign staff to ${r.display_name}`} className="rounded border border-pt-input-border text-pt-text-muted p-1.5 hover:text-pt-cyan hover:border-pt-cyan/40"><Plus size={13} /></button>
                                    </div>
                                    <div className="space-y-1.5">
                                        {rows.map((a) => (
                                            <div key={a.assignment_id} className="rounded border border-pt-border/60 bg-pt-panel px-2.5 py-2 flex items-start gap-2">
                                                <ShieldCheck size={12} className="text-emerald-400 mt-0.5 shrink-0" />
                                                <div className="min-w-0 flex-1"><div className="text-[11px] text-pt-text truncate">{a.staff_name}</div><div className="text-[9px] text-pt-text-muted">{staffRoleLabel(a.role)} · {timeLabel(a.shift_start)}{a.shift_end ? `–${timeLabel(a.shift_end)}` : ''}</div></div>
                                                <button type="button" disabled={removing} onClick={() => onRemove(a.assignment_id)} className="text-pt-text-dim hover:text-red-400 disabled:opacity-40" aria-label={`Remove ${a.staff_name}`}><Trash2 size={12} /></button>
                                            </div>
                                        ))}
                                        {rows.length === 0 && <div className="text-[10px] text-pt-text-dim border border-dashed border-pt-border rounded px-2 py-3 text-center">Coverage unassigned</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {!loading && assignmentRooms.length > 0 && <div className="px-4 pb-4 text-[10px] text-pt-text-dim flex items-center gap-1"><Clock3 size={11} /> {staffedResources.size}/{assignmentRooms.length} ASC rooms, bays, or areas have named coverage.</div>}
            </section>

            {dialogRoom && (
                <AssignmentDialog
                    dateStr={dateStr} initialRoom={dialogRoom} rooms={assignmentRooms}
                    surgeons={surgeons} anesthesia={anesthesia} support={support} allStaff={staff}
                    busy={saving} onClose={() => setDialogRoom(null)}
                    onSave={(input) => { onSave(input); setDialogRoom(null); }}
                />
            )}
        </div>
    );
}

function AssignmentDialog({ dateStr, initialRoom, rooms, surgeons, anesthesia, support, allStaff, busy, onClose, onSave }: {
    dateStr: string;
    initialRoom: RoomResource;
    rooms: RoomResource[];
    surgeons: StaffMember[];
    anesthesia: StaffMember[];
    support: StaffMember[];
    allStaff: StaffMember[];
    busy: boolean;
    onClose: () => void;
    onSave: (input: { dateStr: string; resource: RoomResource; role: StaffRole; staff: StaffMember; shiftStart?: string; shiftEnd?: string; notes?: string }) => void;
}) {
    const [roomId, setRoomId] = useState(initialRoom.resource_id);
    const selectedRoom = rooms.find((r) => r.resource_id === roomId) ?? initialRoom;
    const rolesFor = (resource: RoomResource): StaffRole[] => {
        if (resource.resource_kind === 'preop_bay') return ['preop_rn', 'anesthesiologist', 'crna', 'support'];
        if (resource.resource_kind === 'pacu_bay') return ['pacu_rn', 'anesthesiologist', 'crna', 'support'];
        if (resource.resource_kind === 'discharge_area') return ['discharge_rn', 'support'];
        return ['surgeon', 'anesthesiologist', 'crna', 'circulating_rn', 'scrub_tech', 'support'];
    };
    const defaultRoleFor = (resource: RoomResource): StaffRole => resource.resource_kind === 'preop_bay' ? 'preop_rn'
        : resource.resource_kind === 'pacu_bay' ? 'pacu_rn'
            : resource.resource_kind === 'discharge_area' ? 'discharge_rn' : 'circulating_rn';
    const roles = rolesFor(selectedRoom);
    const [role, setRole] = useState<StaffRole>(defaultRoleFor(initialRoom));
    const candidates = role === 'surgeon' ? surgeons : (role === 'anesthesiologist' || role === 'crna') ? anesthesia : support.length ? support : allStaff.filter((p) => !isSurgeon(p));
    const [staffId, setStaffId] = useState<number | ''>('');
    const [start, setStart] = useState(`${dateStr}T07:00`);
    const [end, setEnd] = useState(`${dateStr}T17:00`);
    const [notes, setNotes] = useState('');
    const selected = candidates.find((p) => p.provider_id === staffId);

    function changeRoom(nextId: string) {
        const next = rooms.find((r) => r.resource_id === nextId);
        setRoomId(nextId);
        if (next && !rolesFor(next).includes(role)) setRole(defaultRoleFor(next));
        setStaffId('');
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/65 flex items-center justify-center p-6">
            <div className="w-full max-w-lg rounded-xl border border-pt-cyan/35 bg-pt-panel p-5">
                <h3 className="text-base font-semibold text-pt-text">Assign clinical coverage</h3>
                <p className="text-xs text-pt-text-muted mt-0.5 mb-4">{dateStr} · OR and recovery staff are rostered independently from patient occupancy.</p>
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs text-pt-text-muted">Resource<select value={roomId} onChange={(e) => changeRoom(e.target.value)} className="mt-1 w-full rounded border border-pt-input-border bg-pt-darker text-pt-text px-3 py-2">{rooms.map((r) => <option key={r.resource_id} value={r.resource_id}>{r.display_name}</option>)}</select></label>
                    <label className="text-xs text-pt-text-muted">Role<select value={role} onChange={(e) => { setRole(e.target.value as StaffRole); setStaffId(''); }} className="mt-1 w-full rounded border border-pt-input-border bg-pt-darker text-pt-text px-3 py-2">{roles.map((r) => <option key={r} value={r}>{staffRoleLabel(r)}</option>)}</select></label>
                    <label className="col-span-2 text-xs text-pt-text-muted">Staff member<select value={staffId} onChange={(e) => setStaffId(Number(e.target.value) || '')} className="mt-1 w-full rounded border border-pt-input-border bg-pt-darker text-pt-text px-3 py-2"><option value="">Select from active roster…</option>{candidates.map((p) => <option key={p.provider_id} value={p.provider_id}>{staffLabel(p)} · {professionalLine(p)}</option>)}</select></label>
                    <label className="text-xs text-pt-text-muted">Shift start<input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-full rounded border border-pt-input-border bg-pt-darker text-pt-text px-3 py-2" /></label>
                    <label className="text-xs text-pt-text-muted">Shift end<input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-full rounded border border-pt-input-border bg-pt-darker text-pt-text px-3 py-2" /></label>
                    <label className="col-span-2 text-xs text-pt-text-muted">Coverage note<textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Relief, call coverage, restrictions…" className="mt-1 w-full rounded border border-pt-input-border bg-pt-darker text-pt-text px-3 py-2" /></label>
                </div>
                <div className="flex justify-end gap-2 mt-5"><button type="button" onClick={onClose} className="text-xs text-pt-text-muted px-3 py-2">Cancel</button><button type="button" disabled={busy || !selected || !start || !end || new Date(end) <= new Date(start)} onClick={() => selected && onSave({ dateStr, resource: selectedRoom, role, staff: selected, shiftStart: new Date(start).toISOString(), shiftEnd: new Date(end).toISOString(), notes })} className="text-xs font-medium rounded bg-pt-gold text-pt-darker px-4 py-2 disabled:opacity-40">{busy ? 'Assigning…' : 'Assign coverage'}</button></div>
            </div>
        </div>
    );
}
